import { endpoints, baseurl } from "../Utils/Constants";

// ---------------------------------------------------------------------------
// In-memory cache for getAllAegisAiDocuments (60-second TTL).
// Only the list endpoint is cached — getAegisAiDocumentById is always live.
// The cache is invalidated automatically whenever a document is saved or
// deleted so the list reflects the latest state after any mutation.
// ---------------------------------------------------------------------------
const _docsListCache = { data: null, token: null, timestamp: 0 };
const DOCS_CACHE_TTL_MS = 60_000; // 60 seconds

const _isCacheValid = (token) =>
    _docsListCache.token === token &&
    _docsListCache.data !== null &&
    Date.now() - _docsListCache.timestamp < DOCS_CACHE_TTL_MS;

export const invalidateAegisAiDocumentsCache = () => {
    _docsListCache.data = null;
    _docsListCache.token = null;
    _docsListCache.timestamp = 0;
};

export const getAllAegisAiDocuments = async (token) => {
    if (_isCacheValid(token)) {
        return _docsListCache.data;
    }
    try {
        const response = await fetch(baseurl + endpoints.GET_ALL_AEGIS_AI_DOCUMENTS, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const json = await response.json();
        // Only cache successful responses.
        if (json?.is_success) {
            _docsListCache.data = json;
            _docsListCache.token = token;
            _docsListCache.timestamp = Date.now();
        }
        return json;
    } catch (error) {
        console.error("Error fetching Aegis AI documents:", error);
        throw error;
    }
};

export const getAegisAiDocumentById = async (id, token) => {
    try {
        const response = await fetch(`${baseurl + endpoints.GET_AEGIS_AI_DOCUMENT_BY_ID}${id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        return await response.json();
    } catch (error) {
        console.error(`Error fetching Aegis AI document with ID ${id}:`, error);
        throw error;
    }
};

export const saveAegisAiDocument = async (token, documentData) => {
    try {
        const { file, media, thumbnail, edited_document: editedDocument, ...payload } = documentData;
        const formData = new FormData();
        formData.append('request', JSON.stringify(payload));

        if (file instanceof File) {
            formData.append('file', file);
        }
        // Audio/video: the (compressed) recording and its thumbnail are stored alongside the transcript.
        if (media instanceof File) {
            formData.append('media', media);
        }
        if (thumbnail instanceof File) {
            formData.append('thumbnail', thumbnail);
        }
        // Documents: the edited HTML, which the API saves as a PDF in place of the original.
        if (editedDocument instanceof File) {
            formData.append('edited_document', editedDocument);
        }
        

        const response = await fetch(`${baseurl}${endpoints.SAVE_AEGIS_AI_DOCUMENT}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });
        const json = await response.json();
        // Invalidate the list cache so the next getAll returns fresh data.
        invalidateAegisAiDocumentsCache();
        return json;
    } catch (error) {
        console.error("Error saving Aegis AI document:", error);
        throw error;
    }
};

export const getAegisAiYoutubePreview = async (url, token, signal) => {
    const response = await fetch(`${baseurl}${endpoints.GET_AEGIS_AI_YOUTUBE_PREVIEW}${encodeURIComponent(url)}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        signal,
    });
    return await response.json();
};

// Stored recording ("media") or its thumbnail ("thumbnail") for an audio/video document, as a Blob.
export const getAegisAiDocumentMedia = async (id, kind, token, signal) => {
    const response = await fetch(`${baseurl}${endpoints.GET_AEGIS_AI_DOCUMENT_MEDIA}${id}?kind=${kind}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        signal,
    });
    // Errors come back as a JSON ResponseModel instead of the file.
    if (!response.ok || (response.headers.get("content-type") || "").includes("application/json")) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.message || "Failed to load media");
    }
    return await response.blob();
};

// Streams the text extracted from a knowledge source (SSE over POST). Events: status -> chunk... (whole sites: page -> chunk...
// per page) -> (transcripts: cleaning -> cleaned...) -> done | error. Pass an AbortSignal to cancel; aborting is silent (no onError).
export const streamExtractAegisAiSource = async (token, { sourceType, url, file, text, wholeSite }, { onStatus, onChunk, onPage, onCleaning, onCleaned, onDone, onError }, signal) => {
    try {
        const formData = new FormData();
        formData.append("request", JSON.stringify({ SourceType: sourceType, Url: url || null, Text: text || null, WholeSite: Boolean(wholeSite) }));
        if (file instanceof File) {
            formData.append("file", file);
        }

        const response = await fetch(`${baseurl}${endpoints.EXTRACT_AEGIS_AI_SOURCE_STREAM}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
            signal,
        });
        if (!response.ok) {
            const errJson = await response.json().catch(() => null);
            throw new Error(errJson?.message || `Server responded with status ${response.status}`);
        }
        // Auth failures come back as a plain JSON ResponseModel instead of a stream.
        if (!(response.headers.get("content-type") || "").includes("text/event-stream")) {
            const json = await response.json().catch(() => null);
            throw new Error(json?.message || "Failed to extract content");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split("\n\n");
            buffer = parts.pop() || "";

            for (const part of parts) {
                const line = part.trim();
                if (!line.startsWith("data: ")) continue;
                let parsed;
                try {
                    parsed = JSON.parse(line.slice(6));
                } catch (e) {
                    console.warn("Failed to parse SSE line:", line, e);
                    continue;
                }
                if (parsed.type === "status") {
                    onStatus && onStatus(parsed.message);
                } else if (parsed.type === "chunk") {
                    onChunk && onChunk(parsed.content);
                } else if (parsed.type === "page") {
                    onPage && onPage(parsed);
                } else if (parsed.type === "cleaning") {
                    onCleaning && onCleaning(parsed.message);
                } else if (parsed.type === "cleaned") {
                    onCleaned && onCleaned(parsed.content);
                } else if (parsed.type === "done") {
                    onDone && onDone(parsed);
                    return;
                } else if (parsed.type === "error") {
                    onError && onError(parsed.message || "Failed to extract content");
                    return;
                }
            }
        }
        onError && onError("The extraction ended unexpectedly. Please try again.");
    } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error extracting Aegis AI source:", error);
        onError && onError(error.message || "Failed to extract content");
    }
};

export const deleteAegisAiDocument = async (id, token) => {
    try {
        const response = await fetch(`${baseurl + endpoints.DELETE_AEGIS_AI_DOCUMENT}${id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const json = await response.json();
        // Invalidate the list cache so the next getAll returns fresh data.
        invalidateAegisAiDocumentsCache();
        return json;
    } catch (error) {
        console.error(`Error deleting Aegis AI document with ID ${id}:`, error);
        throw error;
    }
};

// Passages of the reviewed text likely to cause RAG hallucinations (undefined abbreviations, vague or ambiguous wording...).
export const reviewAegisAiHallucinationRisks = async (token, text, signal) => {
    const response = await fetch(`${baseurl}${endpoints.REVIEW_AEGIS_AI_HALLUCINATION_RISKS}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ Text: text }),
        signal,
    });
    return await response.json();
};

// Cleans up and paraphrases speech transcripts (video, audio, YouTube) to strip stutters, fillers, and reduce hallucinations.
export const cleanAegisAiTranscript = async (token, text, sourceType = "video", signal) => {
    const response = await fetch(`${baseurl}${endpoints.CLEAN_AEGIS_AI_TRANSCRIPT}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text, source_type: sourceType }),
        signal,
    });
    return await response.json();
};

// A newly picked PDF/DOCX ({ file }) or a saved document ({ documentId }) as editable HTML, images embedded.
export const getAegisAiDocumentHtml = async (token, { file, documentId, enableOcr }, signal) => {
    const formData = new FormData();
    if (file instanceof File) formData.append("file", file);
    if (documentId) formData.append("document_id", documentId);
    if (enableOcr) formData.append("enable_ocr", "true");
    const response = await fetch(`${baseurl}${endpoints.GET_AEGIS_AI_DOCUMENT_HTML}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
        signal,
    });
    return await response.json();
};
